import { useCallback, useContext } from 'react';

import {
  DrumkitPlatform,
  SidebarStateContext,
} from 'contexts/sidebarStateContext';
import { useToast } from 'hooks/useToaster';
import {
  DraftReplyProps,
  createMailClientInstance,
  supportsMailClient,
  supportsMailClientSend,
} from 'lib/mailclient/interface';
import captureException from 'utils/captureException';

export enum QuoteReplyAction {
  Draft = 'draft',
  Send = 'send',
}

type ReplyWithMailClientParams = {
  action: QuoteReplyAction;
  reply: DraftReplyProps;
  setLoading: (isLoading: boolean) => void;
  // Runs after the mail client accepts the reply, before the success toast.
  onReplied: () => Promise<void> | void;
  functionName: string;
};

export type UseQuoteReplyMailClientResult = {
  canDraftReply: boolean;
  canSendReply: boolean;
  replyWithMailClient: (params: ReplyWithMailClientParams) => Promise<void>;
};

// Drafts or sends a quote reply through the host mail client (Outlook in vesta,
// the rep's Front composer in verona) so every quote flow shares one path.
export function useQuoteReplyMailClient(): UseQuoteReplyMailClientResult {
  const {
    currentState: { drumkitPlatform, isOutlookReply },
  } = useContext(SidebarStateContext);
  const { toast } = useToast();
  const canDraftReply = supportsMailClient(drumkitPlatform);
  const canSendReply = supportsMailClientSend(drumkitPlatform);

  const replyWithMailClient = useCallback(
    async ({
      action,
      reply,
      setLoading,
      onReplied,
      functionName,
    }: ReplyWithMailClientParams) => {
      const isSend = action === QuoteReplyAction.Send;
      if (isSend ? !canSendReply : !canDraftReply) {
        return;
      }

      setLoading(true);

      try {
        const mailClient = createMailClientInstance(drumkitPlatform);
        if (isSend) {
          await mailClient.sendReply(reply);
        } else {
          await mailClient.draftReply(reply);
        }

        await onReplied();
      } catch (error: unknown) {
        captureException(error, { functionName });
        toast({
          description: isSend
            ? 'Unable to send quote reply.'
            : 'Unable to create draft reply.',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      if (isSend) {
        toast({
          description: 'Successfully sent quote reply.',
          variant: 'success',
        });
        setLoading(false);
        return;
      }

      setTimeout(
        () => {
          toast({
            description: 'Successfully created draft reply.',
            variant: 'success',
          });
          setLoading(false);
        },
        getDraftReplyToastDelayMs(drumkitPlatform, isOutlookReply)
      );
    },
    [canDraftReply, canSendReply, drumkitPlatform, isOutlookReply, toast]
  );

  return { canDraftReply, canSendReply, replyWithMailClient };
}

// Outlook read view shows a new Graph draft a few seconds after it is created;
// Outlook compose view and the Front composer show it right away.
function getDraftReplyToastDelayMs(
  drumkitPlatform: DrumkitPlatform,
  isOutlookReply: boolean
): number {
  if (drumkitPlatform === DrumkitPlatform.Outlook && !isOutlookReply) {
    return 3500;
  }

  return 1;
}
